/* eslint-disable no-underscore-dangle */
import { CameraProfile, CameraViewOrientation } from '@editor/interfaces/camera'
import { Matrix4, PerspectiveCamera, Quaternion, Vector3 } from 'three'
import { ArcballControls as ThreeArcballControls } from 'three/addons/controls/ArcballControls.js'

class ArcballControls extends ThreeArcballControls {
  get gizmoMatrixState() {
    return this._gizmoMatrixState
  }

  get cameraMatrixState() {
    return this._cameraMatrixState
  }

  get gizmos() {
    return this._gizmos
  }

  set currentTarget(target: Vector3) {
    this._currentTarget.copy(target)
  }

  /** Indicates whether the ArcballControls is currently animating the camera. */
  private isAnimating = false

  /**
   * Update the ArcballControls target and related gizmo positions and states
   * without performing a camera transformation. This is useful for cases where we want to
   * instantly update the target (e.g., after camera animation) without causing an additional camera movement.
   *
   * @param target The new target position for the ArcballControls.
   */
  updateTarget(target: Vector3) {
    this.target.copy(target)
    this.currentTarget = target
    this._gizmos.position.copy(target)
    this._tbRadius = this.calculateTbRadius(this.object)
    this.makeGizmos(target, this._tbRadius)
    this.updateMatrixState()
  }

  /**
   * Animates a Three.js camera from its current transform to a target transform
   * using cubic ease-in-out interpolation.
   *
   * Position uses linear interpolation (LERP).
   * Rotation uses quaternion spherical interpolation (SLERP).
   *
   * @param targetPosition Target camera position
   * @param targetQuaternion Target camera quaternion
   * @param duration Duration in milliseconds
   */
  animateCamera(targetPosition: Vector3, targetQuaternion: Quaternion, duration: number): Promise<void> {
    if (!(this.object instanceof PerspectiveCamera))
      return Promise.reject(new Error('animateCamera only supports PerspectiveCamera'))

    const camera = this.object
    const startPosition = camera.position.clone()
    const startQuaternion = camera.quaternion.clone()

    // If duration is zero or negative, immediately apply the target transform
    if (duration <= 0) {
      camera.position.copy(targetPosition)
      camera.quaternion.copy(targetQuaternion)
      camera.updateMatrixWorld()
      return Promise.resolve()
    }

    function easeInOutCubic(t: number): number {
      return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2
    }

    function lerp(a: number, b: number, t: number): number {
      return a + (b - a) * t
    }

    const isPanEnabled = this.enablePan
    const isRotateEnabled = this.enableRotate
    const isZoomEnabled = this.enableZoom

    this.enablePan = false
    this.enableRotate = false
    this.enableZoom = false
    this.isAnimating = true

    return new Promise<void>((resolve) => {
      const startTime = performance.now()

      function frame(now: number) {
        const elapsed = now - startTime
        const t = Math.min(Math.max(elapsed / duration, 0), 1)
        const eased = easeInOutCubic(t)

        camera.position.set(
          lerp(startPosition.x, targetPosition.x, eased),
          lerp(startPosition.y, targetPosition.y, eased),
          lerp(startPosition.z, targetPosition.z, eased),
        )

        camera.quaternion.copy(startQuaternion).slerp(targetQuaternion, eased)

        if (t < 1) {
          requestAnimationFrame(frame)
        } else {
          resolve()
        }
      }

      requestAnimationFrame(frame)
    }).then(() => {
      this.enablePan = isPanEnabled
      this.enableRotate = isRotateEnabled
      this.enableZoom = isZoomEnabled
      this.isAnimating = false
    })
  }

  /**
   * Animate the ArcballControls to a new position.
   *
   * @param target The new target position for the ArcballControls.
   * @param newCameraPosition The new camera position to animate to.
   * @param newCameraQuaternion The new camera orientation (quaternion) to animate to.
   * @param duration The duration of the animation in milliseconds.
   * @returns A promise that resolves when the animation is complete.
   */
  async animateToTarget(
    target: Vector3,
    newCameraPosition: Vector3,
    newCameraQuaternion: Quaternion,
    duration: number = 800,
  ) {
    // Prevent overlapping animations
    if (this.isAnimating) return

    // Animate camera to the new position and orientation
    await this.animateCamera(newCameraPosition, newCameraQuaternion, duration)

    // Update ArcballControls target to the new target
    this.updateTarget(target)
  }

  /**
   * Animate the ArcballControls to a new camera profile.
   *
   * @param profile The camera profile to animate to.
   * @param orientation The camera view orientation to use from the profile.
   * @param duration The duration of the animation in milliseconds. If not provided, a default duration will be used.
   *                 If set to 0, the camera will jump to the new profile without animation.
   * @returns A promise that resolves when the animation is complete.
   */
  async animateToProfile(
    profile: CameraProfile,
    orientation: CameraViewOrientation = CameraViewOrientation.FRONT,
    duration: number = 800,
  ) {
    const target = new Vector3(...profile.target)
    const newCameraPosition = new Vector3(...profile.positions[orientation])
    const newCameraQuaternion = new Quaternion()
    const cameraUp = new Vector3(
      profile.state.arcballState.cameraUp.x,
      profile.state.arcballState.cameraUp.y,
      profile.state.arcballState.cameraUp.z,
    )

    if (orientation === CameraViewOrientation.TOP) {
      cameraUp.fromArray(profile.positions.back).sub(target).normalize()
    } else if (orientation === CameraViewOrientation.BOTTOM) {
      cameraUp.fromArray(profile.positions.front).sub(target).normalize()
    }

    newCameraQuaternion.setFromRotationMatrix(new Matrix4().lookAt(newCameraPosition, target, cameraUp))

    if (duration) {
      await this.animateToTarget(target, newCameraPosition, newCameraQuaternion, duration)
    } else {
      this.focusToTarget(target, newCameraPosition, newCameraQuaternion)
    }
  }

  /**
   * Focus the ArcballControls to a specific target position. This method can be used to instantly move the camera
   * to focus on a new target without animation. It updates the ArcballControls target and related gizmo positions and states.
   *
   * @param target The new target position for the ArcballControls.
   * @param cameraPosition The new camera position to focus on the target.
   * @param cameraQuaternion The new camera orientation (quaternion) to focus on the target.
   */
  focusToTarget(target: Vector3, cameraPosition: Vector3, cameraQuaternion: Quaternion) {
    if (!(this.object instanceof PerspectiveCamera)) return

    this.object.position.copy(cameraPosition)
    this.object.quaternion.copy(cameraQuaternion)
    this.object.updateMatrixWorld()

    this.updateTarget(target)
  }
}
export default ArcballControls
